import { useTranslation } from "react-i18next";
import { ETF_INDEX_RULEBOOKS, type EtfDetail } from "../../../api/services/etf.type";
import { EtfVerdictBadge } from "../EtfScreener/EtfVerdictBadge";
import { formatEtfPercent } from "../EtfScreener/etfFormat";
import { EtfScaledBar } from "./EtfWeightBars";
import styles from "./etfDetail.module.css";

const INDEX_STATUS = {
  djim: "statusDjim",
  ftse: "statusFtse",
  msci: "statusMsci",
  sp: "statusSp",
} as const;

/**
 * The fund under all five rulebooks, in the locked order aaoifi, djim, ftse,
 * msci, sp (handover §16.4). The fund screen's verdict is the bare `status`;
 * the four index rulebooks have no doubtful bucket and no coverage, so those
 * cells say so rather than borrowing the AAOIFI figures (§12.4).
 */
export const EtfRulebooks = ({ fund }: { fund: EtfDetail }) => {
  const { t } = useTranslation();
  const p = "pages.etfDetail.rulebooks.";
  const names = "pages.etfScreener.filters.methodologies.";
  const cols = "pages.etfScreener.columns.";

  const rows = [
    {
      key: "aaoifi",
      status: fund.status,
      halal: fund.compliantWeightPerc,
      notHalal: fund.nonCompliantWeightPerc,
      segments: [
        { tone: "halal" as const, value: fund.compliantWeightPerc },
        { tone: "doubtful" as const, value: fund.doubtfulWeightPerc },
        { tone: "notHalal" as const, value: fund.nonCompliantWeightPerc },
      ],
    },
    ...ETF_INDEX_RULEBOOKS.map((book) => {
      const weights = fund.rulebookWeights[book];
      return {
        key: book,
        status: fund[INDEX_STATUS[book]],
        halal: weights.compliantWeightPerc,
        notHalal: weights.nonCompliantWeightPerc,
        segments: [
          { tone: "halal" as const, value: weights.compliantWeightPerc },
          { tone: "notHalal" as const, value: weights.nonCompliantWeightPerc },
        ],
      };
    }),
  ];

  return (
    <section className={`${styles.card} ${styles.cardWide}`}>
      <h2 className={styles.cardTitle}>{t(`${p}title`)}</h2>
      <div className={styles.scrollX}>
        <table className={styles.rulebooks}>
          <thead>
            <tr>
              <th scope="col">{t(`${p}rulebook`)}</th>
              <th scope="col">{t(`${p}verdict`)}</th>
              <th scope="col" className={styles.num}>
                {t(`${cols}halalWeight`)}
              </th>
              <th scope="col" className={styles.num}>
                {t(`${cols}notHalalWeight`)}
              </th>
              <th scope="col" className={styles.rulebookBar}>
                {t(`${p}split`)}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td className={styles.rulebookName}>{t(`${names}${row.key}`)}</td>
                <td>
                  <EtfVerdictBadge status={row.status} compact />
                </td>
                <td className={styles.num}>{formatEtfPercent(row.halal)}</td>
                <td className={styles.num}>{formatEtfPercent(row.notHalal)}</td>
                <td className={styles.rulebookBar}>
                  <EtfScaledBar segments={row.segments} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.note}>{t(`${p}scaleNote`)}</p>
      <p className={styles.note}>{t("pages.etfScreener.seoContent.disagreeParagraph")}</p>
    </section>
  );
};
